import { Component, Input, OnInit } from '@angular/core';

@Component({
    selector: 'app-note-details-tab',
    templateUrl: './note-details-tab.component.html',
    styleUrls: ['./note-details-tab.component.scss'],
    standalone: false
})
export class NoteDetailsTabComponent implements OnInit {
  @Input() noteData: any;
  @Input() displayMode: 'full' | 'summary' = 'full';
  @Input() statuses: any[] = [];
  @Input() statusId: number | null = null;
  @Input() priorityValue: number | string | null = null;

  constructor() {}

  ngOnInit(): void {
    // Initialize component
  }

  getModuleName(): string {
    return this.noteData?.module_name || '-';
  }

  getReferenceName(): string {
    return this.noteData?.reference_name || '-';
  }

  getCreatedBy(): string {
    return this.noteData?.created_by_name || '-';
  }

  getCreatedOn(): Date | null {
    return this.noteData?.created_at
      ? new Date(this.noteData.created_at)
      : null;
  }

  getUpdatedOn(): Date | null {
    return this.noteData?.updated_at
      ? new Date(this.noteData.updated_at)
      : null;
  }

  getMentions(): any[] {
    return this.noteData?.mentions || [];
  }

  getAttachmentCount(): number {
    return this.noteData?.attachment_ids?.length || 0;
  }

  getStatusLabel(): string {
    return (
      this.noteData?.status_label?.name ||
      this.noteData?.status_label ||
      this.noteData?.status ||
      '-'
    );
  }

  getPriorityLabel(): string {
    const priority = Number(this.noteData?.priority);
    const priorityMap: { [key: number]: string } = {
      1: 'Low',
      2: 'Medium',
      3: 'High',
      4: 'Urgent',
    };
    return (
      this.noteData?.priority_label ||
      priorityMap[priority] ||
      this.noteData?.priority ||
      '-'
    );
  }

  getCreatedByName(): string {
    return (
      this.noteData?.created_by?.full_name ||
      this.noteData?.created_by_name ||
      `${this.noteData?.created_by?.first_name || ''} ${
        this.noteData?.created_by?.last_name || ''
      }`.trim() ||
      '-'
    );
  }

  getDescription(): string {
    return this.noteData?.description || 'No description available';
  }

  // ===========================================================================
  // SUMMARY HELPERS (EDIT MODE)
  // ===========================================================================

  getSummaryStatusName(): string {
    const statusFromList = this.findStatusById();
    if (statusFromList?.name) {
      return statusFromList.name;
    }
    return this.getStatusLabel();
  }

  getSummaryStatusColor(): string | null {
    const statusFromList = this.findStatusById();
    if (statusFromList?.color) {
      return statusFromList.color;
    }
    return this.noteData?.status_label?.color || null;
  }

  getStatusPillClass(): string {
    const color = this.getSummaryStatusColor();
    if (color) {
      return 'status-pill status-pill--custom';
    }

    const statusName = this.normalize(this.getSummaryStatusName());
    const themeMap: { [key: string]: string } = {
      new: 'status-pill status-pill--info',
      open: 'status-pill status-pill--info',
      review: 'status-pill status-pill--warning',
      pending: 'status-pill status-pill--warning',
      completed: 'status-pill status-pill--success',
      done: 'status-pill status-pill--success',
      closed: 'status-pill status-pill--neutral',
      cancelled: 'status-pill status-pill--neutral',
    };

    return themeMap[statusName] || 'status-pill status-pill--default';
  }

  getSummaryPriorityLabel(): string {
    const priorityMap: { [key: number]: string } = {
      1: 'Low',
      2: 'Medium',
      3: 'High',
      4: 'Urgent',
    };

    if (typeof this.priorityValue === 'number') {
      return priorityMap[this.priorityValue] || this.priorityValue.toString();
    }

    if (typeof this.priorityValue === 'string') {
      const trimmed = this.priorityValue.trim();
      const asNumber = Number(trimmed);
      if (!Number.isNaN(asNumber)) {
        return priorityMap[asNumber] || trimmed;
      }
      return this.capitalize(trimmed);
    }

    return this.getPriorityLabel();
  }

  getPriorityPillClass(): string {
    const normalized = this.normalize(this.getSummaryPriorityLabel());
    const classMap: { [key: string]: string } = {
      low: 'priority-pill priority-pill--low',
      medium: 'priority-pill priority-pill--medium',
      high: 'priority-pill priority-pill--high',
      critical: 'priority-pill priority-pill--critical',
      urgent: 'priority-pill priority-pill--critical',
    };
    return classMap[normalized] || 'priority-pill priority-pill--medium';
  }

  private findStatusById(): any | null {
    if (!this.statusId || !Array.isArray(this.statuses)) {
      return null;
    }
    return (
      this.statuses.find((status: any) => status?.id === this.statusId) || null
    );
  }

  private normalize(value: string | null | undefined): string {
    return (value || '').toString().toLowerCase().trim();
  }

  private capitalize(value: string): string {
    if (!value) return '';
    const lower = value.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }
}
